import React, { useEffect, useRef, useState } from "react";
import { Select, MenuItem } from "@material-ui/core";
import moment from "moment";
import { EditorState, convertToRaw } from "draft-js";
import { Editor } from "react-draft-wysiwyg";
import draftToHtml from "draftjs-to-html";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import RiskStratificationService from "../../service/RiskStratificationService";
import EvaluationModal from "./EvaluationModal";
import {
  RiskBadge,
  KeyDriversList,
  RecommendedActionsList,
} from "./RiskSummaryBlocks";
import {
  getRiskClass,
  riskSummaryToHtml,
  keyDriversToHtml,
  recommendedActionsToHtml,
  htmlToEditorState,
  RISK_LEVEL_OPTIONS,
  EVALUATION_STATUS_CORRECT,
  EVALUATION_STATUS_INCORRECT,
  EVALUATION_EDITOR_TOOLBAR,
} from "../../utils/riskUtils";

const service = new RiskStratificationService();

const REASON_MAX_WORDS = 500;

const countWords = (s) => (s.trim() ? s.trim().split(/\s+/).length : 0);

// Caps the reason at REASON_MAX_WORDS, trimming a paste that runs over rather
// than rejecting it outright.
const capWords = (s) =>
  countWords(s) <= REASON_MAX_WORDS
    ? s
    : s.trim().split(/\s+/).slice(0, REASON_MAX_WORDS).join(" ");

const VERDICTS = [
  {
    value: EVALUATION_STATUS_CORRECT,
    label: "Correct",
    hint: "The AI summary aligns with the case summary",
    className: "correct",
  },
  {
    value: EVALUATION_STATUS_INCORRECT,
    label: "Incorrect",
    hint: "Refine the evaluator summary, risk drivers and recommendations",
    className: "incorrect",
  },
];

const toHtml = (state) => draftToHtml(convertToRaw(state.getCurrentContent()));
const isEditorEmpty = (state) => !state.getCurrentContent().hasText();

// Seeds an editor from the evaluator's saved HTML, falling back to the AI's own
// output so the doctor refines the summary rather than retyping it.
const seedEditor = (savedHtml, fallbackHtml) =>
  htmlToEditorState(savedHtml) ||
  htmlToEditorState(fallbackHtml) ||
  EditorState.createEmpty();

/**
 * The AI risk summary review-and-evaluate flow, saved against the risk
 * stratification record exactly as the risk-stratification detail page saves
 * it, so the evaluation, its audit trail and the version comparison stay in
 * one place whichever screen it was made from.
 *
 * mode "view"     — read-only summary ("Viewing AI risk summary").
 * mode "evaluate" — Correct / Incorrect, then the refine step when incorrect.
 *
 * onSaved(payload) fires after the risk service accepts the evaluation, with
 * the body that was saved, so a host screen can mirror the verdict.
 */
function AIEvaluationDialog({
  open,
  mode = "evaluate",
  record,
  evaluatorName,
  onClose,
  onSaved,
}) {
  const [step, setStep] = useState("review");
  const [verdict, setVerdict] = useState(0);
  const [updatedRiskLevel, setUpdatedRiskLevel] = useState(0);
  const [reason, setReason] = useState("");
  const [summary, setSummary] = useState(EditorState.createEmpty());
  const [drivers, setDrivers] = useState(EditorState.createEmpty());
  const [recommendations, setRecommendations] = useState(
    EditorState.createEmpty(),
  );
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  // Synchronous double-click guard; `saving` alone has a window before React
  // commits the disabled attribute.
  const savingRef = useRef(false);

  useEffect(() => {
    if (!open || !record) return;
    setStep("review");
    setVerdict(0);
    setError("");
    setUpdatedRiskLevel(record.updatedRiskLevel || record.riskLevel || 0);
    setReason(record.evaluationReason || "");
    setSummary(
      seedEditor(record.evaluatorSummary, riskSummaryToHtml(record.riskSummary)),
    );
    setDrivers(
      seedEditor(record.keyRiskDriversHtml, keyDriversToHtml(record.keyDrivers)),
    );
    setRecommendations(
      seedEditor(
        record.evaluatorRecommendations,
        recommendedActionsToHtml(record.recommendedActions),
      ),
    );
  }, [open, record]);

  if (!open || !record) return null;

  const isEvaluate = mode === "evaluate";
  const isIncorrect = verdict === EVALUATION_STATUS_INCORRECT;

  const handleClose = () => {
    if (savingRef.current) return;
    if (onClose) onClose();
  };

  const buildPayload = () => {
    if (isIncorrect) {
      return {
        evaluationStatus: EVALUATION_STATUS_INCORRECT,
        evaluationReason: reason.trim(),
        updatedRiskLevel,
        evaluatorSummary: toHtml(summary),
        keyRiskDriversHtml: toHtml(drivers),
        evaluatorRecommendations: toHtml(recommendations),
      };
    }
    // Confirming the AI means the AI's own output is the evaluated content.
    return {
      evaluationStatus: EVALUATION_STATUS_CORRECT,
      evaluationReason: "",
      updatedRiskLevel: record.riskLevel,
      evaluatorSummary: riskSummaryToHtml(record.riskSummary),
      keyRiskDriversHtml: keyDriversToHtml(record.keyDrivers),
      evaluatorRecommendations: recommendedActionsToHtml(
        record.recommendedActions,
      ),
    };
  };

  const validate = () => {
    if (!isIncorrect) return "";
    if (!updatedRiskLevel) return "Select the updated risk level.";
    if (!reason.trim()) return "Add a reason for the incorrect evaluation.";
    if (
      isEditorEmpty(summary) ||
      isEditorEmpty(drivers) ||
      isEditorEmpty(recommendations)
    )
      return "Fill the evaluator summary, key risk drivers and recommendations.";
    return "";
  };

  const handleSave = () => {
    if (savingRef.current) return;
    const problem = validate();
    if (problem) {
      setError(problem);
      return;
    }
    const payload = buildPayload();
    savingRef.current = true;
    setSaving(true);
    setError("");

    const fail = (err) => {
      console.error("Error saving AI evaluation:", err);
      savingRef.current = false;
      setSaving(false);
      setError(
        err?.message ||
          err?.error ||
          "Could not save the evaluation. Please try again.",
      );
    };
    const persist = () =>
      service.saveEvaluation(
        record.id,
        payload,
        () => {
          savingRef.current = false;
          setSaving(false);
          if (onSaved) onSaved(payload);
        },
        fail,
      );

    // Take ownership first so the risk dashboard's evaluator column names
    // whoever evaluated from here. It is idempotent, and a failure must not
    // block the evaluation itself.
    service.assignEvaluator(record.id, persist, persist);
  };

  const footerNote = `Evaluating as ${evaluatorName || "you"} · ${moment().format(
    "D MMM YYYY",
  )}`;

  const renderEditor = (label, state, setState, placeholder) => (
    <div className="ai-eval-editor-block">
      <h5 className="ai-eval-editor-label">{label}</h5>
      <Editor
        editorState={state}
        wrapperClassName="ai-eval-editor-wrapper"
        editorClassName="ai-eval-editor-content"
        toolbarClassName="ai-eval-editor-toolbar"
        toolbar={EVALUATION_EDITOR_TOOLBAR}
        onEditorStateChange={setState}
        placeholder={placeholder}
      />
    </div>
  );

  const reviewStep = (
    <>
      <section className="ai-eval-card">
        <div className="ai-eval-card-title-row">
          <h4 className="ai-eval-card-title">AI Risk Summary</h4>
          <RiskBadge level={record.riskLevel} />
        </div>
        {record.riskSummary && (
          <p className="ai-eval-summary-text">{record.riskSummary}</p>
        )}
        {Array.isArray(record.keyDrivers) && record.keyDrivers.length > 0 && (
          <>
            <div className="ai-eval-divider" />
            <h5 className="ai-eval-subtitle">Key Risk Drivers</h5>
            <KeyDriversList drivers={record.keyDrivers} />
          </>
        )}
      </section>

      {/* Once a verdict is picked the dialog narrows to the summary and the
          question, so the choice and its Continue stay in view. */}
      {(!isEvaluate || !verdict) &&
        Array.isArray(record.recommendedActions) &&
        record.recommendedActions.length > 0 && (
          <section className="ai-eval-card">
            <h4 className="ai-eval-card-title">Recommended Actions</h4>
            <RecommendedActionsList actions={record.recommendedActions} />
          </section>
        )}

      {isEvaluate && (
        <section className="ai-eval-card">
          <h4 className="ai-eval-card-title">
            Is the AI risk assessment correct?
          </h4>
          <div className="ai-eval-verdicts" role="radiogroup">
            {VERDICTS.map((v) => {
              const selected = verdict === v.value;
              return (
                <button
                  key={v.value}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  className={`ai-eval-verdict ${v.className}${
                    selected ? " selected" : ""
                  }`}
                  onClick={() => {
                    setVerdict(v.value);
                    setError("");
                  }}
                >
                  <span className="ai-eval-radio" />
                  <span className="ai-eval-verdict-copy">
                    <strong>{v.label}</strong>
                    <small>{v.hint}</small>
                  </span>
                </button>
              );
            })}
          </div>
        </section>
      )}
    </>
  );

  const refineStep = (
    <>
      <section className="ai-eval-card">
        <h4 className="ai-eval-card-title">Your evaluation</h4>
        <label className="ai-eval-field-label">Updated risk level</label>
        <Select
          value={updatedRiskLevel}
          onChange={(e) => setUpdatedRiskLevel(parseInt(e.target.value, 10) || 0)}
          displayEmpty
          disableUnderline
          className={`ai-eval-risk-select ${getRiskClass(updatedRiskLevel)}`}
        >
          <MenuItem value={0} disabled>
            Select
          </MenuItem>
          {RISK_LEVEL_OPTIONS.map((o) => (
            <MenuItem key={o.value} value={o.value}>
              {o.label}
            </MenuItem>
          ))}
        </Select>

        <label className="ai-eval-field-label">
          Reason for incorrect evaluation
          <span className="ai-eval-required">*</span>
        </label>
        <div className="ai-eval-reason">
          <input
            type="text"
            value={reason}
            placeholder="Explain why the AI assessment is incorrect..."
            onChange={(e) => setReason(capWords(e.target.value))}
          />
          <span className="ai-eval-reason-count">
            max {REASON_MAX_WORDS} words
          </span>
        </div>
      </section>

      {renderEditor(
        "Evaluator Summary",
        summary,
        setSummary,
        "Type evaluator summary here...",
      )}
      {renderEditor(
        "Key Risk Drivers",
        drivers,
        setDrivers,
        "Type key risk drivers here...",
      )}
      <div className="ai-eval-divider" />
      {renderEditor(
        "Evaluator recommendations",
        recommendations,
        setRecommendations,
        "Type evaluator recommendations here...",
      )}
    </>
  );

  // Review step: Cancel until a verdict is picked, then Back (which clears it).
  // Continue on Correct saves the AI summary as it stands; on Incorrect it
  // opens the refine step.
  let actions;
  if (step === "review") {
    actions = verdict ? (
      <>
        <button
          type="button"
          className="ai-eval-btn"
          onClick={() => {
            setVerdict(0);
            setError("");
          }}
          disabled={saving}
        >
          Back
        </button>
        <button
          type="button"
          className="ai-eval-btn primary"
          disabled={saving}
          onClick={() => (isIncorrect ? setStep("refine") : handleSave())}
        >
          {saving ? "Saving..." : "Continue"}
        </button>
      </>
    ) : (
      <>
        <button type="button" className="ai-eval-btn" onClick={handleClose}>
          Cancel
        </button>
        <button type="button" className="ai-eval-btn primary" disabled>
          Continue
        </button>
      </>
    );
  } else {
    actions = (
      <>
        <button
          type="button"
          className="ai-eval-btn"
          onClick={() => {
            setStep("review");
            setError("");
          }}
          disabled={saving}
        >
          Back
        </button>
        <button
          type="button"
          className="ai-eval-btn primary"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? "Saving..." : "Save evaluation"}
        </button>
      </>
    );
  }

  return (
    <EvaluationModal open={open} onClose={handleClose}>
      <div className="ai-eval-header">
        <h3 className="ai-eval-title">
          {isEvaluate ? "Evaluate AI risk summary" : "Viewing AI risk summary"}
        </h3>
        <button
          type="button"
          className="ai-eval-close"
          onClick={handleClose}
          aria-label="Close"
        >
          ✕
        </button>
      </div>

      <div className="ai-eval-body">
        {step === "refine" && isEvaluate ? refineStep : reviewStep}
      </div>

      {/* Viewing is read-only and has no footer; close with ✕ or Escape. */}
      {isEvaluate && (
        <div className="ai-eval-footer">
          <span className="ai-eval-footer-note">{footerNote}</span>
          <div className="ai-eval-footer-actions">
            {error && <span className="ai-eval-error">{error}</span>}
            {actions}
          </div>
        </div>
      )}
    </EvaluationModal>
  );
}

export default AIEvaluationDialog;
